import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export interface FinancialData {
  id?: string;
  name?: string;
  revenue: number;
  previousRevenue: number;
  totalCosts: number;
  currentAssets: number;
  currentLiabilities: number;
  timestamp?: number;
}

export interface Scenario extends FinancialData {
  id: string;
  name: string;
  timestamp: number;
}

interface FinancialContextType {
  financialData: FinancialData;
  scenarios: Scenario[];
  updateData: (newData: Partial<FinancialData>) => void;
  saveCurrentAsScenario: (name: string) => void;
  loadScenario: (id: string) => void;
  deleteScenario: (id: string) => void;
  resetToDefault: () => void;
}

const DATA_STORAGE_KEY = 'v2_financial_decision_data';
const SCENARIOS_STORAGE_KEY = 'v2_financial_scenarios';

const defaultData: FinancialData = {
  revenue: 1250000,
  previousRevenue: 1100000,
  totalCosts: 850000,
  currentAssets: 500000,
  currentLiabilities: 300000,
};

const FinancialContext = createContext<FinancialContextType | undefined>(undefined);

export function FinancialProvider({ children }: { children: ReactNode }) {
  // 1. Current Working Data
  const [financialData, setFinancialData] = useState<FinancialData>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(DATA_STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed to parse financial data from storage", e);
        }
      }
    }
    return defaultData;
  });

  // 2. Saved Scenarios
  const [scenarios, setScenarios] = useState<Scenario[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SCENARIOS_STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed to parse scenarios from storage", e);
        }
      }
    }
    return [];
  });

  // Persist Current Data
  useEffect(() => {
    localStorage.setItem(DATA_STORAGE_KEY, JSON.stringify(financialData));
  }, [financialData]);

  // Persist Scenarios
  useEffect(() => {
    localStorage.setItem(SCENARIOS_STORAGE_KEY, JSON.stringify(scenarios));
  }, [scenarios]);

  const updateData = (newData: Partial<FinancialData>) => {
    setFinancialData(prev => ({ ...prev, ...newData }));
  };

  const saveCurrentAsScenario = (name: string) => {
    const newScenario: Scenario = {
      ...financialData,
      id: crypto.randomUUID(),
      name,
      timestamp: Date.now()
    };
    setScenarios(prev => [newScenario, ...prev]);
  };

  const loadScenario = (id: string) => {
    const scenario = scenarios.find(s => s.id === id);
    if (scenario) {
      const { id: _, name: __, timestamp: ___, ...data } = scenario;
      setFinancialData(data);
    }
  };

  const deleteScenario = (id: string) => {
    setScenarios(prev => prev.filter(s => s.id !== id));
  };

  const resetToDefault = () => {
    setFinancialData(defaultData);
  };

  return (
    <FinancialContext.Provider value={{ 
      financialData, 
      scenarios,
      updateData, 
      saveCurrentAsScenario,
      loadScenario,
      deleteScenario,
      resetToDefault 
    }}>
      {children}
    </FinancialContext.Provider>
  );
}

export function useFinancialData() {
  const context = useContext(FinancialContext);
  if (context === undefined) {
    throw new Error('useFinancialData must be used within a FinancialProvider');
  }
  return context;
}

